import React, { useState } from 'react';
import { Lock, Mail, LogIn, FolderOpen, Clock, CheckCircle2, AlertCircle, TrendingUp, FileText } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { mockProjects } from '../mockData';

const ClientPortal = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });

  const handleLogin = (e) => {
    e.preventDefault();
    // MOCK LOGIN - Will be replaced with actual authentication
    if (loginData.email && loginData.password) {
      setIsLoggedIn(true);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'En cours': return 'text-cyan-400';
      case 'Terminé': return 'text-green-400';
      case 'Planifié': return 'text-yellow-400';
      default: return 'text-gray-400';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'En cours': return <TrendingUp size={20} />;
      case 'Terminé': return <CheckCircle2 size={20} />;
      case 'Planifié': return <Clock size={20} />;
      default: return <AlertCircle size={20} />;
    }
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4 pt-20">
        <Card className="w-full max-w-md bg-slate-900 border-slate-800">
          <CardHeader className="text-center space-y-4 pb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-teal-500/20 to-cyan-500/20 rounded-2xl flex items-center justify-center mx-auto">
              <Lock className="text-teal-400" size={32} />
            </div>
            <CardTitle className="text-3xl font-bold text-white">
              Portail <span className="bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">Client</span>
            </CardTitle>
            <p className="text-gray-400">Accédez à vos projets et ressources</p>
          </CardHeader>
          <CardContent className="pb-8">
            <form onSubmit={handleLogin} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Adresse email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={20} />
                  <input
                    type="email"
                    value={loginData.email}
                    onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                    className="w-full bg-slate-800 border border-slate-700 rounded-lg pl-11 pr-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                    placeholder="votre@email.com"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Mot de passe
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={20} />
                  <input
                    type="password"
                    value={loginData.password}
                    onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                    className="w-full bg-slate-800 border border-slate-700 rounded-lg pl-11 pr-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                    placeholder="••••••••"
                    required
                  />
                </div>
              </div>
              <div className="flex items-center justify-between text-sm">
                <label className="flex items-center text-gray-400 cursor-pointer">
                  <input type="checkbox" className="mr-2 rounded" />
                  Se souvenir de moi
                </label>
                <a href="#" className="text-teal-400 hover:text-teal-300 transition-colors">
                  Mot de passe oublié ?
                </a>
              </div>
              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white py-6 text-lg font-medium shadow-lg hover:shadow-teal-500/50 transition-all duration-300 group"
              >
                Se connecter
                <LogIn className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
              </Button>
            </form>
            <div className="mt-6 text-center">
              <p className="text-gray-400 text-sm">
                Pas encore de compte ?{' '}
                <a href="/contact" className="text-teal-400 hover:text-teal-300 transition-colors font-medium">
                  Contactez-nous
                </a>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 pt-24 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-12 animate-in fade-in duration-700">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2">
              Bienvenue sur votre <span className="bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">Portail</span>
            </h1>
            <p className="text-gray-400">Gérez vos projets et suivez leur avancement en temps réel</p>
          </div>
          <Button
            onClick={() => setIsLoggedIn(false)}
            variant="outline"
            className="border-teal-500/50 text-teal-400 hover:bg-teal-500/10"
          >
            Déconnexion
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-4 gap-6 mb-12">
          <Card className="bg-slate-900 border-slate-800 hover:border-teal-500/50 transition-all duration-300 group">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm mb-1">Projets Actifs</p>
                  <p className="text-3xl font-bold text-white">2</p>
                </div>
                <div className="w-12 h-12 bg-gradient-to-br from-teal-500/20 to-cyan-500/20 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                  <FolderOpen className="text-teal-400" size={24} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-900 border-slate-800 hover:border-cyan-500/50 transition-all duration-300 group">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm mb-1">Terminés</p>
                  <p className="text-3xl font-bold text-white">1</p>
                </div>
                <div className="w-12 h-12 bg-gradient-to-br from-green-500/20 to-emerald-500/20 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                  <CheckCircle2 className="text-green-400" size={24} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-900 border-slate-800 hover:border-yellow-500/50 transition-all duration-300 group">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm mb-1">Planifiés</p>
                  <p className="text-3xl font-bold text-white">1</p>
                </div>
                <div className="w-12 h-12 bg-gradient-to-br from-yellow-500/20 to-amber-500/20 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Clock className="text-yellow-400" size={24} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-900 border-slate-800 hover:border-purple-500/50 transition-all duration-300 group">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm mb-1">Documents</p>
                  <p className="text-3xl font-bold text-white">12</p>
                </div>
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500/20 to-violet-500/20 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                  <FileText className="text-purple-400" size={24} />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Projects List */}
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-white mb-6">Vos Projets</h2>
          {mockProjects.map((project) => (
            <Card key={project.id} className="bg-slate-900 border-slate-800 hover:border-teal-500/50 transition-all duration-300 group">
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between mb-4">
                  <div className="mb-4 md:mb-0">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-xl font-bold text-white group-hover:text-teal-400 transition-colors">
                        {project.name}
                      </h3>
                      <span className={`flex items-center space-x-1 text-sm font-medium ${getStatusColor(project.status)}`}>
                        {getStatusIcon(project.status)}
                        <span>{project.status}</span>
                      </span>
                    </div>
                    <p className="text-gray-400 text-sm">{project.description}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-400">Client</p>
                    <p className="text-white font-medium">{project.client}</p>
                  </div>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Progression</span>
                    <span className="text-teal-400 font-semibold">{project.progress}%</span>
                  </div>
                  <Progress value={project.progress} className="h-2" />
                </div>

                <div className="flex items-center justify-between text-sm">
                  <div className="flex space-x-6">
                    <div>
                      <span className="text-gray-400">Début: </span>
                      <span className="text-white">{new Date(project.startDate).toLocaleDateString('fr-FR')}</span>
                    </div>
                    <div>
                      <span className="text-gray-400">Échéance: </span>
                      <span className="text-white">{new Date(project.dueDate).toLocaleDateString('fr-FR')}</span>
                    </div>
                  </div>
                  <Button className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white">
                    Voir détails
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ClientPortal;